import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import appConfig from './config/appConfig';

const GradientBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50"></div>
    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-200/30 to-transparent rounded-full blur-3xl"></div>
  </div>
);

const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}>
    {children}
  </div>
);

const MultiSelectDropdown = ({ label, options, selectedValues, onChange, placeholder = "Select..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
    ? selectedValues[0]
    : `${selectedValues.length} selected`;

  return (
    <div ref={dropdownRef} className="relative z-[60]">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
      >
        <span className={selectedValues.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
          {displayText}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
          <div
            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-200 flex items-center space-x-2 sticky top-0 bg-white z-[201]"
            onClick={handleSelectAll}
          >
            <input
              type="checkbox"
              checked={selectedValues.length === options.length}
              onChange={() => {}}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-gray-700">
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </span>
          </div>

          {options.map((option) => (
            <div
              key={option}
              className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center space-x-2"
              onClick={() => handleToggle(option)}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => {}}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">{option}</span>
            </div>
          ))}

          {options.length === 0 && (
            <div className="px-4 py-2 text-gray-500 text-sm">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductClassificationReport = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [aggregatedData, setAggregatedData] = useState([]);
  const [productTotals, setProductTotals] = useState({});
  const [classificationTotals, setClassificationTotals] = useState({});
  const [summaryStats, setSummaryStats] = useState({
    total_requirements: 0,
    classified_requirements: 0,
    unclassified_requirements: 0,
    total_classifications: 0
  });
  const [allStatuses, setAllStatuses] = useState([]);
  const [allPriorities, setAllPriorities] = useState([]);
  const [allCriticalities, setAllCriticalities] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allClassifications, setAllClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilters, setStatusFilters] = useState([]);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [criticalityFilters, setCriticalityFilters] = useState([]);
  const [productFilters, setProductFilters] = useState([]);
  const [classificationFilters, setClassificationFilters] = useState([]);

  const corporate_account = localStorage.getItem('corporate_account');
  const current_project = localStorage.getItem('current_project');
  const login_token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!current_project || current_project === 'null') {
      setError('Please select a project from the Requirements Portal');
      return;
    }
    fetchReportData();
  }, [current_project]);

  const fetchReportData = async (customFilters = null) => {
    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        corporate_account,
        project_id: current_project
      };

      const statusesToUse = customFilters !== null ? customFilters.status : statusFilters;
      const prioritiesToUse = customFilters !== null ? customFilters.priority : priorityFilters;
      const criticalityToUse = customFilters !== null ? customFilters.criticality : criticalityFilters;
      const productsToUse = customFilters !== null ? customFilters.product : productFilters;
      const classificationsToUse = customFilters !== null ? customFilters.classification : classificationFilters;

      if (statusesToUse && statusesToUse.length > 0) requestBody.status = statusesToUse;
      if (prioritiesToUse && prioritiesToUse.length > 0) requestBody.priority = prioritiesToUse;
      if (criticalityToUse && criticalityToUse.length > 0) requestBody.criticality = criticalityToUse;
      if (productsToUse && productsToUse.length > 0) requestBody.product = productsToUse;
      if (classificationsToUse && classificationsToUse.length > 0) requestBody.classification = classificationsToUse;

      const response = await fetch(appConfig.getUrl('/get_product_classification_report'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setReportData(data.report_data);
        setAggregatedData(data.aggregated_data);
        setProductTotals(data.product_totals);
        setClassificationTotals(data.classification_totals);
        setSummaryStats(data.summary_stats);
        setAllStatuses(data.all_statuses || []);
        setAllPriorities(data.all_priorities || []);
        setAllCriticalities(data.all_criticalities || []);
        setAllProducts(data.all_products || []);
        setAllClassifications(data.all_classifications || []);
      } else {
        setError(data.status_description);
      }
    } catch (err) {
      setError('Failed to fetch report data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchReportData();
  };

  const handleRefresh = () => {
    fetchReportData();
  };

  const handleClearFilters = () => {
    setStatusFilters([]);
    setPriorityFilters([]);
    setCriticalityFilters([]);
    setProductFilters([]);
    setClassificationFilters([]);

    fetchReportData({
      status: [],
      priority: [],
      criticality: [],
      product: [],
      classification: []
    });
  };

  const hasActiveFilters = statusFilters.length > 0 || priorityFilters.length > 0 ||
                          criticalityFilters.length > 0 || productFilters.length > 0 ||
                          classificationFilters.length > 0;
  const totalActiveFilters = statusFilters.length + priorityFilters.length +
                            criticalityFilters.length + productFilters.length +
                            classificationFilters.length;

  // Build table data structure
  const buildTableData = () => {
    const tableData = {};

    // Get all classifications from data and ensure "Unclassified" is always included
    const classificationsFromData = [...new Set(aggregatedData.map(item => item.classification))];
    if (!classificationsFromData.includes('Unclassified')) {
      classificationsFromData.push('Unclassified');
    }

    // Sort classifications with Unclassified at the end
    const displayClassifications = classificationsFromData
      .filter(c => c !== 'Unclassified')
      .sort()
      .concat(['Unclassified']);

    // Get products from aggregatedData, excluding any "Totals" entries
    const displayProducts = [...new Set(aggregatedData.map(item => item.product_name))]
      .filter(product => product && product.toLowerCase() !== 'totals')
      .sort();

    displayProducts.forEach(product => {
      tableData[product] = {};
      displayClassifications.forEach(classification => {
        tableData[product][classification] = 0;
      });
    });

    aggregatedData.forEach(item => {
      if (item.product_name && item.product_name.toLowerCase() !== 'totals' &&
          tableData[item.product_name] && displayClassifications.includes(item.classification)) {
        tableData[item.product_name][item.classification] = item.count;
      }
    });

    return { tableData, displayProducts, displayClassifications };
  };

  const buildChartData = () => {
    const { tableData, displayProducts, displayClassifications } = buildTableData();
    const chartData = [];

    displayProducts.forEach(product => {
      const dataPoint = { product };
      displayClassifications.forEach(classification => {
        dataPoint[classification] = tableData[product][classification];
      });
      chartData.push(dataPoint);
    });

    return { chartData, displayClassifications };
  };

  const { tableData, displayProducts, displayClassifications } = buildTableData();
  const { chartData } = buildChartData();

  const generateClassificationColors = () => {
    const baseColors = [
      '#3b82f6', // blue-500
      '#06b6d4', // cyan-500
      '#0ea5e9', // sky-500
      '#60a5fa', // blue-400
      '#22d3ee', // cyan-400
      '#38bdf8', // sky-400
      '#2563eb', // blue-600
      '#0891b2', // cyan-600
      '#0284c7', // sky-600
      '#1d4ed8'  // blue-700
    ];

    const colors = {};
    displayClassifications.forEach((classification, index) => {
      colors[classification] = baseColors[index % baseColors.length];
    });
    return colors;
  };

  const classificationColors = generateClassificationColors();

  if (!current_project || current_project === 'null') {
    return (
      <div className="min-h-screen">
        <GradientBackground />
        <Header />
        <div className="max-w-7xl mx-auto mt-24 px-4">
          <GlassCard className="p-8 text-center">
            <div className="text-amber-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Project Selected</h2>
            <p className="text-gray-600 mb-6">Please select a project from the Requirements Portal to view reports.</p>
            <button
              onClick={() => navigate('/RequirementsPortal')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium"
            >
              Go to Requirements Portal
            </button>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <GradientBackground />
      <Header />

      <div className="max-w-7xl mx-auto mt-24 px-4 pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Product Classification Report</h1>
                  <p className="text-sm text-gray-600">Requirements distribution across products and classifications</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Refreshing...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Refresh</span>
                  </span>
                )}
              </button>
            </div>
          </GlassCard>
        </div>

        {/* Filters Section */}
        <div className="mb-16 relative z-50">
          <GlassCard className="p-6 overflow-visible relative z-50">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </h2>
              {hasActiveFilters && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {totalActiveFilters} Filter{totalActiveFilters !== 1 ? 's' : ''} Active
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 relative z-[60]">
              <MultiSelectDropdown
                label="Status"
                options={allStatuses}
                selectedValues={statusFilters}
                onChange={setStatusFilters}
                placeholder="All Statuses"
              />

              <MultiSelectDropdown
                label="Priority"
                options={allPriorities}
                selectedValues={priorityFilters}
                onChange={setPriorityFilters}
                placeholder="All Priorities"
              />

              <MultiSelectDropdown
                label="Criticality"
                options={allCriticalities}
                selectedValues={criticalityFilters}
                onChange={setCriticalityFilters}
                placeholder="All Criticalities"
              />

              <MultiSelectDropdown
                label="Product"
                options={allProducts}
                selectedValues={productFilters}
                onChange={setProductFilters}
                placeholder="All Products"
              />

              <MultiSelectDropdown
                label="Classification"
                options={allClassifications}
                selectedValues={classificationFilters}
                onChange={setClassificationFilters}
                placeholder="All Classifications"
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Apply Filters</span>
                </span>
              </button>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Clear All Filters</span>
                  </span>
                </button>
              )}
            </div>

            {hasActiveFilters && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Active Filters:</p>
                <div className="flex flex-wrap gap-2">
                  {statusFilters.map(status => (
                    <span key={status} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Status: {status}</span>
                      <button onClick={() => setStatusFilters(statusFilters.filter(s => s !== status))} className="ml-1 hover:text-blue-900">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {priorityFilters.map(priority => (
                    <span key={priority} className="px-3 py-1 bg-cyan-200 text-cyan-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Priority: {priority}</span>
                      <button onClick={() => setPriorityFilters(priorityFilters.filter(p => p !== priority))} className="ml-1 hover:text-cyan-900">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {criticalityFilters.map(criticality => (
                    <span key={criticality} className="px-3 py-1 bg-sky-200 text-sky-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Criticality: {criticality}</span>
                      <button onClick={() => setCriticalityFilters(criticalityFilters.filter(c => c !== criticality))} className="ml-1 hover:text-sky-900">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {productFilters.map(product => (
                    <span key={product} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Product: {product}</span>
                      <button onClick={() => setProductFilters(productFilters.filter(p => p !== product))} className="ml-1 hover:text-blue-900">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {classificationFilters.map(classification => (
                    <span key={classification} className="px-3 py-1 bg-cyan-200 text-cyan-800 rounded-full text-sm flex items-center space-x-1">
                      <span>Classification: {classification}</span>
                      <button onClick={() => setClassificationFilters(classificationFilters.filter(c => c !== classification))} className="ml-1 hover:text-cyan-900">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>
        </div>

        {error && (
          <div className="mb-8">
            <GlassCard className="p-4 bg-red-50 border-red-200">
              <div className="flex items-center space-x-2 text-red-700">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            </GlassCard>
          </div>
        )}

        {loading ? (
          <GlassCard className="p-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <svg className="animate-spin h-12 w-12 text-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Loading report data...</p>
            </div>
          </GlassCard>
        ) : (
          <>
            {/* Cross-Tab Table */}
            <div className="mb-8">
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Product vs Classification Matrix</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border-2 border-blue-300 shadow-lg rounded-lg overflow-hidden">
                    <thead>
                      <tr>
                        <th className="border-2 border-white bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 text-left text-white font-bold text-lg">
                          Product
                        </th>
                        <th colSpan={displayClassifications.length + 1} className="border-2 border-white bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4 text-center text-white font-bold text-lg">
                          Classification Type
                        </th>
                      </tr>
                      <tr className="bg-blue-100">
                        <th className="border-2 border-white bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-4 text-left text-white font-bold text-lg"></th>
                        {displayClassifications.map(classification => (
                          <th key={classification} className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 px-6 py-4 text-center text-blue-700 font-bold text-lg">
                            {classification}
                          </th>
                        ))}
                        <th className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 px-6 py-4 text-center text-blue-700 font-bold text-lg">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayProducts.map((product, idx) => (
                        <tr key={product} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                          <td className="border-2 border-blue-100 px-6 py-4 font-bold text-gray-800 text-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                            {product}
                          </td>
                          {displayClassifications.map(classification => (
                            <td key={classification} className="border-2 border-blue-100 px-6 py-4 text-center text-gray-700 text-lg">
                              {tableData[product][classification]}
                            </td>
                          ))}
                          <td className="border-2 border-blue-100 px-6 py-4 text-center font-semibold text-blue-600 text-lg">
                            {productTotals[product] || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </div>

            {/* Charts Section - Individual Pie Charts for Each Product */}
            <div className="mb-8">
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Classification Distribution by Product</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {displayProducts.map(product => {
                    const productData = displayClassifications
                      .map(classification => ({
                        name: classification,
                        value: tableData[product][classification]
                      }))
                      .filter(item => item.value > 0);

                    return (
                      <div key={product} className="flex flex-col items-center">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">{product}</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={productData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {productData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={classificationColors[entry.name]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductClassificationReport;